import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { MedicineService } from '../../../service/admin/medicine.service';
import { ToasterService } from '../../../service/common/toaster.service';

interface Medicine {
  id?: string | number;
  name: string;
  potency: string;
  code: string;
}

@Component({
  selector: 'app-medicine',
  standalone: true,
  imports: [FormsModule, CommonModule, DropdownModule, InputTextModule, ToastModule, ConfirmDialogModule, DialogModule],
  providers: [ConfirmationService, MedicineService],
  templateUrl: './medicine.component.html',
  styleUrls: ['./medicine.component.scss']
})
export class MedicineComponent implements OnInit, OnDestroy {
  searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription!: Subscription;

  alphabet: string[] = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  letters: string[] = [];
  selectedLetter: string = 'All';

  medicines: Medicine[] = [];
  displayDialog = false;
  codeError = false;
  medicine: Medicine = { name: '', potency: '', code: '' };

  searchTerm: string = '';
  scaleFilter: string = 'all';
  currentPage: number = 1;
  pageSize: number = 10;

  readonly scaleFilterOptions = [
    { label: 'All Scales', value: 'all' },
    { label: 'Mother Tincture', value: 'mother' },
    { label: 'Decimal', value: 'decimal' },
    { label: 'Centesimal', value: 'centesimal' },
    { label: 'LM', value: 'lm' }];

  potencyScales = [
    { label: 'Mother Tincture', value: 'mother' },
    { label: 'Decimal (X)', value: 'decimal' },
    { label: 'Centesimal (C)', value: 'centesimal' },
    { label: 'LM Potencies', value: 'lm' }
  ];

  potencyMap: { [key: string]: string[] } = {
    mother: ['Ø', 'Q'],
    decimal: ['1X', '2X', '3X', '4X', '5X', '6X', '8X', '10X', '12X', '15X', '20X', '30X', '50X', '100X', '200X', '500X', '1000X'],
    centesimal: ['3C', '6C', '12C', '18C', '24C', '30C', '50C', '100C', '200C', '1M', '2M', '5M', '10M', '20M', '30M', '50M', 'CM', 'DM', 'MM'],
    lm: ['LM1', 'LM2', 'LM3', 'LM4', 'LM5', 'LM6', 'LM7', 'LM8', 'LM9', 'LM10', 'LM11', 'LM12', 'LM13', 'LM14', 'LM15', 'LM16', 'LM17', 'LM18', 'LM19', 'LM20', 'LM21', 'LM22', 'LM23', 'LM24', 'LM25', 'LM26', 'LM27', 'LM28', 'LM29', 'LM30']
  };

  selectedScale: string | null = null;
  availablePotencies: string[] = [];

  constructor(
    private toaster: ToasterService, private confirmationService: ConfirmationService,
    private medicineService: MedicineService
  ) {}

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(800),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
    });

    this.letters = ['All', ...this.alphabet];
    this.loadMedicines();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  loadMedicines(): void {
    this.medicineService.getMedicine().subscribe((res: any) => {
      this.medicines = Array.isArray(res?.data) ? res.data : [];
      this.currentPage = 1;
    });
  }

  getPotencyScale(potency: string): string {
    const p = String(potency || '').trim().toUpperCase();
    if (!p) return 'unknown';
    if (p === 'Ø' || p === 'Q') return 'mother';
    if (p.startsWith('LM')) return 'lm';
    if (p.endsWith('X')) return 'decimal';
    return 'centesimal';
  }

  get filteredMedicines(): Medicine[] {
    const search = (this.searchTerm || '').trim().toLowerCase();
    const scale = (this.scaleFilter || 'all').toLowerCase();
    const letter = (this.selectedLetter || 'All').toUpperCase();

    return (this.medicines || []).filter((m) => {
      const name = String(m.name || '');
      const startsWithLetter = letter === 'ALL' || name.toUpperCase().startsWith(letter);
      if (!startsWithLetter) return false;

      const matchesScale = scale === 'all' || this.getPotencyScale(m.potency) === scale;
      if (!matchesScale) return false;

      if (!search) return true;
      const haystack = [m.name, m.potency, m.code].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredMedicines.length / this.pageSize));
  }

  get paginatedMedicines(): Medicine[] {
    const safePage = Math.min(this.currentPage, this.totalPages);
    if (safePage !== this.currentPage) this.currentPage = safePage;
    const start = (safePage - 1) * this.pageSize;
    return this.filteredMedicines.slice(start, start + this.pageSize);
  }

  get visiblePageNumbers(): number[] {
    const radius = 2;
    const start = Math.max(1, this.currentPage - radius);
    const end = Math.min(this.totalPages, this.currentPage + radius);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get paginationStart(): number {
    if (!this.filteredMedicines.length) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredMedicines.length);
  }

  getScaleLabel(potency: string): string {
    const scale = this.getPotencyScale(potency);
    switch (scale) {
      case 'mother':
        return 'Mother Tincture';
      case 'decimal':
        return 'Decimal';
      case 'centesimal':
        return 'Centesimal';
      case 'lm':
        return 'LM';
      default:
        return 'Unknown';
    }
  }

  getScaleClass(potency: string): string {
    return `scale-${this.getPotencyScale(potency)}`;
  }

  selectLetter(letter: string): void {
    this.selectedLetter = letter;
    this.currentPage = 1;
  }

  onFiltersChanged(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterDropdownChange(event: { value?: string }, field: 'scaleFilter'): void {
    if (event?.value !== undefined && event?.value !== null) {
      this[field] = event.value;
    }
    this.currentPage = 1;
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.scaleFilter = 'all';
    this.selectedLetter = 'All';
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  closeForm() {
    this.displayDialog = false;
    this.selectedScale = null;
    this.availablePotencies = [];
    this.codeError = false;
    this.medicine = { name: '', potency: '', code: '' };
  }

  openDialog(): void {
    this.medicine = { name: '', potency: '', code: '' };
    this.codeError = false;
    this.selectedScale = null;
    this.availablePotencies = [];
    this.displayDialog = true;
  }

  onScaleChange(): void {
    if (this.selectedScale) {
      this.availablePotencies = this.potencyMap[this.selectedScale];
      if (!this.medicine.id || !this.medicine.potency) {
        this.medicine.potency = '';
      }
    } else {
      this.availablePotencies = [];
      this.medicine.potency = '';
    }
  }

  onMedicineCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizeMedicineCode(input.value);
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    this.medicine.code = sanitized;
    this.codeError = false;
  }

  private sanitizeMedicineCode(value: unknown): string {
    return String(value ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 20);
  }

  private buildMedicinePayload(): Medicine {
    const payload: Medicine = {
      ...this.medicine,
      name: (this.medicine.name ?? '').trim(),
      potency: (this.medicine.potency ?? '').trim(),
    };
    const code = this.sanitizeMedicineCode(this.medicine.code);
    if (code) {
      payload.code = code;
    } else {
      delete (payload as unknown as { code?: string }).code;
    }
    return payload;
  }

  setScaleFromPotency(potency: string): void {
    if (!potency) return;
    if (potency === 'Ø' || potency === 'Q') {
      this.selectedScale = 'mother';
    } else if (potency.endsWith('X')) {
      this.selectedScale = 'decimal';
    } else if (potency.startsWith('LM')) {
      this.selectedScale = 'lm';
    } else {
      this.selectedScale = 'centesimal';
    }
    this.onScaleChange();
  }

  isCodeDuplicate(code: string): boolean {
    if (!Array.isArray(this.medicines)) return false;
    const trimmedCode = (code ?? '').trim().toUpperCase();
    if (!trimmedCode) return false;
    return this.medicines.some((med) =>
      (med.code ?? '').trim().toUpperCase() === trimmedCode &&
      med.id !== this.medicine.id
    );
  }

  processMedicine() {
    const name = (this.medicine.name ?? '').trim();
    const potency = (this.medicine.potency ?? '').trim();
    const code = this.sanitizeMedicineCode(this.medicine.code);

    if (!name || !potency || !this.selectedScale) {
      this.toaster.warn('Medicine name and potency are required.', 'Incomplete');
      return;
    }

    if (this.isCodeDuplicate(code)) {
      this.codeError = true;
      this.toaster.error('This medicine code is already in use.', 'Duplicate Code');
      return;
    }

    this.codeError = false;

    if (this.medicine?.id) {
      this.updateMedicine();
    } else {
      this.addMedicine();
    }
  }

  addMedicine() {
    this.medicineService.addMedicine(this.buildMedicinePayload()).subscribe(() => {
      this.toaster.success('Medicine created successfully.');
      this.closeForm();
      this.loadMedicines();
    });
  }

  updateMedicine() {
    this.medicineService.updateMedicine(this.buildMedicinePayload()).subscribe(() => {
      this.toaster.success('Medicine updated successfully.');
      this.closeForm();
      this.loadMedicines();
    });
  }

  startEdit(item: Medicine): void {
    this.medicine = { ...item, code: this.sanitizeMedicineCode(item.code) };
    this.codeError = false;
    if (this.medicine.potency) {
      this.setScaleFromPotency(this.medicine.potency);
    }
    this.displayDialog = true;
  }

  confirmDelete(item: Medicine): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.name}"?`,
      header: 'Delete Medicine',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteMedicine(item),
    });
  }

  deleteMedicine(item: Medicine) {
    this.medicineService.deleteMedicine(item).subscribe(() => {
      this.toaster.success('Medicine deleted successfully.');
      this.loadMedicines();
    });
  }
}
